import { Box, Button, Typography, TypographyProps } from "@mui/material";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

interface Props {
    text?: string | null;
    variant?: TypographyProps["variant"];
    maxLines?: number;
    emptyText?: string;
}

export default function AppExpandableText({
    text,
    variant = "body1",
    maxLines = 3,
    emptyText = "Brak",
}: Props) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const measurementRef = useRef<HTMLSpanElement>(null);
    const contentId = useId();
    const displayText = text && text.trim() ? text : emptyText;

    const measureOverflow = useCallback(() => {
        const element = measurementRef.current;
        if (!element) return;

        const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight);
        if (!Number.isFinite(lineHeight)) return;

        setIsOverflowing(element.scrollHeight > lineHeight * maxLines + 1);
    }, [maxLines]);

    useLayoutEffect(() => {
        measureOverflow();
    }, [displayText, measureOverflow, variant]);

    useEffect(() => {
        setExpanded(false);
    }, [displayText]);

    useEffect(() => {
        const element = measurementRef.current;
        if (!element) return;

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(measureOverflow);
            observer.observe(element);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", measureOverflow);
        return () => window.removeEventListener("resize", measureOverflow);
    }, [measureOverflow]);

    return (
        <>
            <Box sx={{ position: "relative" }}>
                <Typography
                    id={contentId}
                    variant={variant}
                    sx={{
                        display: expanded ? "block" : "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: expanded ? "unset" : maxLines,
                        overflow: expanded ? "visible" : "hidden",
                        overflowWrap: "anywhere",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {displayText}
                </Typography>
                <Typography
                    ref={measurementRef}
                    aria-hidden="true"
                    variant={variant}
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "auto",
                        overflowWrap: "anywhere",
                        pointerEvents: "none",
                        visibility: "hidden",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {displayText}
                </Typography>
            </Box>
            {isOverflowing && (
                <Box sx={{ mt: 0.5 }}>
                    <Button
                        type="button"
                        variant="text"
                        size="small"
                        disableElevation
                        disableRipple
                        aria-controls={contentId}
                        aria-expanded={expanded}
                        onClick={() => setExpanded(current => !current)}
                        sx={{
                            minHeight: 44,
                            minWidth: 0,
                            px: 0,
                            border: 0,
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                textDecoration: 'underline',
                            },
                            '&:active': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                opacity: 0.7,
                            },
                            '&:focus': {
                                boxShadow: 'none',
                            },
                            '&.Mui-focusVisible': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                outline: 'none',
                                textDecoration: 'underline',
                                textDecorationThickness: '2px',
                                textUnderlineOffset: '4px',
                            },
                        }}
                    >
                        {expanded ? "Pokaż mniej" : "Pokaż więcej"}
                    </Button>
                </Box>
            )}
        </>
    );
}
